import {
  FinancialsSummaryScope,
  OngoingReportingType,
  ReportingFlow,
  ReportingPeriods,
} from '@common/interfaces/bbc'
import { IApi } from '../../api'
import { createRequestTypes } from '../../helpers/helpers'
import { createAction } from '../types'

export const prefix = 'ongoingReporting'

export const [
  LIST_ONGOING_REPORTING_REQUEST,
  LIST_ONGOING_REPORTING_SUCCESS,
  LIST_ONGOING_REPORTING_FAILURE,
] = createRequestTypes(prefix, 'LIST_ONGOING_REPORTING')

export const listOngoingReporting = createAction((params: object) => ({
  type: [
    LIST_ONGOING_REPORTING_REQUEST,
    LIST_ONGOING_REPORTING_SUCCESS,
    LIST_ONGOING_REPORTING_FAILURE,
  ],
  promise: (api: IApi) => api.ongoingReporting.list(params),
  params,
}))

export const [
  SHOW_ONGOING_REPORTING_REQUEST,
  SHOW_ONGOING_REPORTING_SUCCESS,
  SHOW_ONGOING_REPORTING_FAILURE,
] = createRequestTypes(prefix, 'SHOW_ONGOING_REPORTING')

export const showOngoingReporting = createAction((id: string) => ({
  type: [
    SHOW_ONGOING_REPORTING_REQUEST,
    SHOW_ONGOING_REPORTING_SUCCESS,
    SHOW_ONGOING_REPORTING_FAILURE,
  ],
  promise: (api: IApi) => api.ongoingReporting.show(id),
}))

export const HIDE_ONGOING_REPORTING = `${prefix}/HIDE_ONGOING_REPORTING`

export const hideOngoingReporting = () => ({
  type: HIDE_ONGOING_REPORTING,
})

export const [
  UPDATE_ONGOING_REPORTING_REQUEST,
  UPDATE_ONGOING_REPORTING_SUCCESS,
  UPDATE_ONGOING_REPORTING_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_ONGOING_REPORTING')

export const updateOngoingReporting = createAction((id: string, data: object | FormData) => ({
  type: [
    UPDATE_ONGOING_REPORTING_REQUEST,
    UPDATE_ONGOING_REPORTING_SUCCESS,
    UPDATE_ONGOING_REPORTING_FAILURE,
  ],
  promise: (api: IApi) => api.ongoingReporting.update(id, data),
}))

export const [
  UPDATE_ONGOING_REPORTING_STATUS_REQUEST,
  UPDATE_ONGOING_REPORTING_STATUS_SUCCESS,
  UPDATE_ONGOING_REPORTING_STATUS_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_ONGOING_REPORTING_STATUS')

export const updateOngoingReportingStatus = createAction((data: object) => ({
  type: [
    UPDATE_ONGOING_REPORTING_STATUS_REQUEST,
    UPDATE_ONGOING_REPORTING_STATUS_SUCCESS,
    UPDATE_ONGOING_REPORTING_STATUS_FAILURE,
  ],
  promise: (api: IApi) => api.ongoingReporting.updateReportingStatus(data),
}))

export const [
  UPDATE_ONGOING_REPORTING_AS_OF_DATE_REQUEST,
  UPDATE_ONGOING_REPORTING_AS_OF_DATE_SUCCESS,
  UPDATE_ONGOING_REPORTING_AS_OF_DATE_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_ONGOING_REPORTING_AS_OF_DATE')

export const updateOngoingReportingAsOfDate = createAction(
  (id: string, data: { asOfDate: string }) => ({
    type: [
      UPDATE_ONGOING_REPORTING_AS_OF_DATE_REQUEST,
      UPDATE_ONGOING_REPORTING_AS_OF_DATE_SUCCESS,
      UPDATE_ONGOING_REPORTING_AS_OF_DATE_FAILURE,
    ],
    promise: (api: IApi) => api.ongoingReporting.updateAsOfDate(id, data),
  }),
)

export const [
  SUBMIT_ONGOING_REPORTING_REQUEST,
  SUBMIT_ONGOING_REPORTING_SUCCESS,
  SUBMIT_ONGOING_REPORTING_FAILURE,
] = createRequestTypes(prefix, 'SUBMIT_ONGOING_REPORTING')

export const submitOngoingReporting = createAction((id: string, data: object) => ({
  type: [
    SUBMIT_ONGOING_REPORTING_REQUEST,
    SUBMIT_ONGOING_REPORTING_SUCCESS,
    SUBMIT_ONGOING_REPORTING_FAILURE,
  ],
  promise: (api: IApi) => api.ongoingReporting.submit(id, data),
  id,
}))

export const [
  DELETE_ONGOING_REPORTING_REQUEST,
  DELETE_ONGOING_REPORTING_SUCCESS,
  DELETE_ONGOING_REPORTING_FAILURE,
] = createRequestTypes(prefix, 'DELETE_ONGOING_REPORTING')

export const deleteOngoingReporting = createAction((id: string) => ({
  type: [
    DELETE_ONGOING_REPORTING_REQUEST,
    DELETE_ONGOING_REPORTING_SUCCESS,
    DELETE_ONGOING_REPORTING_FAILURE,
  ],
  promise: (api: IApi) => api.ongoingReporting.delete(id),
  id,
}))

export const [
  LIST_CONSOLIDATED_ONGOING_REPORTING_REQUEST,
  LIST_CONSOLIDATED_ONGOING_REPORTING_SUCCESS,
  LIST_CONSOLIDATED_ONGOING_REPORTING_FAILURE,
] = createRequestTypes(prefix, 'LIST_CONSOLIDATED_ONGOING_REPORTING')

export const listConsolidatedOngoingReporting = createAction((id: string, params: object) => ({
  type: [
    LIST_CONSOLIDATED_ONGOING_REPORTING_REQUEST,
    LIST_CONSOLIDATED_ONGOING_REPORTING_SUCCESS,
    LIST_CONSOLIDATED_ONGOING_REPORTING_FAILURE,
  ],
  promise: (api: IApi) => api.ongoingReporting.listConsolidated(id, params),
  params,
}))

export const [
  LIST_CONSOLIDATED_ONGOING_REPORTING_MULTIPLE_REPORTS_REQUEST,
  LIST_CONSOLIDATED_ONGOING_REPORTING_MULTIPLE_REPORTS_SUCCESS,
  LIST_CONSOLIDATED_ONGOING_REPORTING_MULTIPLE_REPORTS_FAILURE,
] = createRequestTypes(prefix, 'LIST_CONSOLIDATED_ONGOING_REPORTING_MULTIPLE_REPORTS')

export const listConsolidatedOngoingReportingMultipleReports = createAction((params: object) => ({
  type: [
    LIST_CONSOLIDATED_ONGOING_REPORTING_MULTIPLE_REPORTS_REQUEST,
    LIST_CONSOLIDATED_ONGOING_REPORTING_MULTIPLE_REPORTS_SUCCESS,
    LIST_CONSOLIDATED_ONGOING_REPORTING_MULTIPLE_REPORTS_FAILURE,
  ],
  promise: (api: IApi) => api.ongoingReporting.listConsolidatedMultipleReports(params),
  params,
}))

// Params of the financials drill-down request. The api middleware dispatches
// REQUEST and SUCCESS with the same `params` object reference, so the
// `financialsDrillDown` reducer compares identity to match each response to
// the most recent request (last-request-wins, stale responses discarded).
export interface IFinancialsDrillDownParams {
  statementType: OngoingReportingType
  startDate?: string | null
  endDate?: string | null
  perPage: number
  reportingPeriod: ReportingPeriods
  reportingFlow: ReportingFlow
  isMultipleOngoingReporting: boolean
  ongoingReportingIds: string[]
  ddReportingIds: string[]
  opsReportingIds: string[]
}

export const [
  LIST_FINANCIALS_DRILL_DOWN_REQUEST,
  LIST_FINANCIALS_DRILL_DOWN_SUCCESS,
  LIST_FINANCIALS_DRILL_DOWN_FAILURE,
] = createRequestTypes(prefix, 'LIST_FINANCIALS_DRILL_DOWN')

// Drill-down (expandable sub-rows) data for the financials summary tables.
// Same endpoint as the mapping modal's consolidated list, but stored in its
// own slice so the modal's paginated/filtered requests and the summary's
// full-dataset requests never overwrite each other.
export const listFinancialsDrillDown = createAction(
  (params: IFinancialsDrillDownParams, summaryScope?: FinancialsSummaryScope) => ({
    type: [
      LIST_FINANCIALS_DRILL_DOWN_REQUEST,
      LIST_FINANCIALS_DRILL_DOWN_SUCCESS,
      LIST_FINANCIALS_DRILL_DOWN_FAILURE,
    ],
    promise: (api: IApi) => api.ongoingReporting.listConsolidatedMultipleReports(params),
    params: { ...params, summaryScope },
  }),
)

export const HIDE_FINANCIALS_DRILL_DOWN = `${prefix}/HIDE_FINANCIALS_DRILL_DOWN`

export const hideFinancialsDrillDown = (
  statementType: OngoingReportingType,
  summaryScope?: FinancialsSummaryScope,
) => ({
  type: HIDE_FINANCIALS_DRILL_DOWN,
  params: { statementType, summaryScope },
})

export const [
  UPDATE_CONSOLIDATED_ONGOING_REPORTING_REQUEST,
  UPDATE_CONSOLIDATED_ONGOING_REPORTING_SUCCESS,
  UPDATE_CONSOLIDATED_ONGOING_REPORTING_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_CONSOLIDATED_ONGOING_REPORTING')

export const [
  MARK_AS_SEEN_ONGOING_REPORTING_REQUEST,
  MARK_AS_SEEN_ONGOING_REPORTING_SUCCESS,
  MARK_AS_SEEN_ONGOING_REPORTING_FAILURE,
] = createRequestTypes(prefix, 'MARK_AS_SEEN_ONGOING_REPORTING')

export const markAsSeenOngoingReporting = createAction(
  (id: string, clientName: string, categories: string[], statementType: string) => ({
    type: [
      MARK_AS_SEEN_ONGOING_REPORTING_REQUEST,
      MARK_AS_SEEN_ONGOING_REPORTING_SUCCESS,
      MARK_AS_SEEN_ONGOING_REPORTING_FAILURE,
    ],
    promise: (api: IApi) =>
      api.ongoingReporting.markAsSeen(id, {
        clientName,
        categories,
        statementType,
      }),
    id,
  }),
)

export const [
  LIST_REPORTED_TOTAL_CANDIDATES_REQUEST,
  LIST_REPORTED_TOTAL_CANDIDATES_SUCCESS,
  LIST_REPORTED_TOTAL_CANDIDATES_FAILURE,
] = createRequestTypes(prefix, 'LIST_REPORTED_TOTAL_CANDIDATES')

/** The statement's derived rows, so a reviewer can say which one a figure is. */
export const listReportedTotalCandidates = createAction((id: string, data: object) => ({
  type: [
    LIST_REPORTED_TOTAL_CANDIDATES_REQUEST,
    LIST_REPORTED_TOTAL_CANDIDATES_SUCCESS,
    LIST_REPORTED_TOTAL_CANDIDATES_FAILURE,
  ],
  promise: (api: IApi) => api.ongoingReporting.listReportedTotalCandidates(id, data),
}))

export const [
  LIST_RECONCILIATION_REVIEW_REQUEST,
  LIST_RECONCILIATION_REVIEW_SUCCESS,
  LIST_RECONCILIATION_REVIEW_FAILURE,
] = createRequestTypes(prefix, 'LIST_RECONCILIATION_REVIEW')

/** How each reconciliation check on a statement is built, for the Review view. */
export const listReconciliationReview = createAction((id: string, data: object) => ({
  type: [
    LIST_RECONCILIATION_REVIEW_REQUEST,
    LIST_RECONCILIATION_REVIEW_SUCCESS,
    LIST_RECONCILIATION_REVIEW_FAILURE,
  ],
  promise: (api: IApi) => api.ongoingReporting.listReconciliationReview(id, data),
}))

export const [
  EXPLAIN_RECONCILIATION_DIFFERENCE_REQUEST,
  EXPLAIN_RECONCILIATION_DIFFERENCE_SUCCESS,
  EXPLAIN_RECONCILIATION_DIFFERENCE_FAILURE,
] = createRequestTypes(prefix, 'EXPLAIN_RECONCILIATION_DIFFERENCE')

/** Why one month of a reconciliation check doesn't tie. */
export const explainReconciliationDifference = createAction((id: string, data: object) => ({
  type: [
    EXPLAIN_RECONCILIATION_DIFFERENCE_REQUEST,
    EXPLAIN_RECONCILIATION_DIFFERENCE_SUCCESS,
    EXPLAIN_RECONCILIATION_DIFFERENCE_FAILURE,
  ],
  promise: (api: IApi) => api.ongoingReporting.explainReconciliationDifference(id, data),
}))

export const [
  LIST_RECONCILIATION_ACCEPTANCES_REQUEST,
  LIST_RECONCILIATION_ACCEPTANCES_SUCCESS,
  LIST_RECONCILIATION_ACCEPTANCES_FAILURE,
] = createRequestTypes(prefix, 'LIST_RECONCILIATION_ACCEPTANCES')

/** The accepted differences on a statement that still stand. */
export const listReconciliationAcceptances = createAction((id: string, data: object) => ({
  type: [
    LIST_RECONCILIATION_ACCEPTANCES_REQUEST,
    LIST_RECONCILIATION_ACCEPTANCES_SUCCESS,
    LIST_RECONCILIATION_ACCEPTANCES_FAILURE,
  ],
  promise: (api: IApi) => api.ongoingReporting.listReconciliationAcceptances(id, data),
}))

export const [
  ACCEPT_RECONCILIATION_DIFFERENCE_REQUEST,
  ACCEPT_RECONCILIATION_DIFFERENCE_SUCCESS,
  ACCEPT_RECONCILIATION_DIFFERENCE_FAILURE,
] = createRequestTypes(prefix, 'ACCEPT_RECONCILIATION_DIFFERENCE')

/** Accept one month's reconciliation difference with a reason. */
export const acceptReconciliationDifference = createAction((id: string, data: object) => ({
  type: [
    ACCEPT_RECONCILIATION_DIFFERENCE_REQUEST,
    ACCEPT_RECONCILIATION_DIFFERENCE_SUCCESS,
    ACCEPT_RECONCILIATION_DIFFERENCE_FAILURE,
  ],
  promise: (api: IApi) => api.ongoingReporting.acceptReconciliationDifference(id, data),
}))

export const [
  REMOVE_RECONCILIATION_ACCEPTANCE_REQUEST,
  REMOVE_RECONCILIATION_ACCEPTANCE_SUCCESS,
  REMOVE_RECONCILIATION_ACCEPTANCE_FAILURE,
] = createRequestTypes(prefix, 'REMOVE_RECONCILIATION_ACCEPTANCE')

/** Take back the acceptance of one month's reconciliation difference. */
export const removeReconciliationAcceptance = createAction((id: string, data: object) => ({
  type: [
    REMOVE_RECONCILIATION_ACCEPTANCE_REQUEST,
    REMOVE_RECONCILIATION_ACCEPTANCE_SUCCESS,
    REMOVE_RECONCILIATION_ACCEPTANCE_FAILURE,
  ],
  promise: (api: IApi) => api.ongoingReporting.removeReconciliationAcceptance(id, data),
}))

export const RETRY_PREDICTIONS = createRequestTypes(prefix, 'RETRY_PREDICTIONS')

/** Re-run the category prediction for one statement. */
export const retryPredictions = createAction((id: string, data: object) => ({
  type: RETRY_PREDICTIONS,
  promise: (api: IApi) => api.ongoingReporting.retryPredictions(id, data),
}))

export const updateConsolidatedOngoingReporting = createAction((id: string, data: object) => ({
  type: [
    UPDATE_CONSOLIDATED_ONGOING_REPORTING_REQUEST,
    UPDATE_CONSOLIDATED_ONGOING_REPORTING_SUCCESS,
    UPDATE_CONSOLIDATED_ONGOING_REPORTING_FAILURE,
  ],
  promise: (api: IApi) => api.ongoingReporting.updateConsolidated(id, data),
}))

export const [
  LIST_SUMMARY_ONGOING_REPORTING_REQUEST,
  LIST_SUMMARY_ONGOING_REPORTING_SUCCESS,
  LIST_SUMMARY_ONGOING_REPORTING_FAILURE,
] = createRequestTypes(prefix, 'LIST_SUMMARY_ONGOING_REPORTING')

export const listSummaryOngoingReporting = createAction((id: string, data: object) => ({
  type: [
    LIST_SUMMARY_ONGOING_REPORTING_REQUEST,
    LIST_SUMMARY_ONGOING_REPORTING_SUCCESS,
    LIST_SUMMARY_ONGOING_REPORTING_FAILURE,
  ],
  promise: (api: IApi) => api.ongoingReporting.listSummary(id, data),
}))

export const [
  LIST_INVENTORY_MAPPING_REQUEST,
  LIST_INVENTORY_MAPPING_SUCCESS,
  LIST_INVENTORY_MAPPING_FAILURE,
] = createRequestTypes(prefix, 'LIST_INVENTORY_MAPPING')

export const listInventoryMapping = createAction((id: string, params: object) => ({
  type: [
    LIST_INVENTORY_MAPPING_REQUEST,
    LIST_INVENTORY_MAPPING_SUCCESS,
    LIST_INVENTORY_MAPPING_FAILURE,
  ],
  promise: (api: IApi) => api.ongoingReporting.listInventoryMapping(id, params),
  params,
}))

export const [
  LIST_SALES_BY_SKU_VISUALIZATION_REQUEST,
  LIST_SALES_BY_SKU_VISUALIZATION_SUCCESS,
  LIST_SALES_BY_SKU_VISUALIZATION_FAILURE,
] = createRequestTypes(prefix, 'LIST_SALES_BY_SKU_VISUALIZATION')

export const listSalesBySkuVisualization = createAction((id: string, params: object) => ({
  type: [
    LIST_SALES_BY_SKU_VISUALIZATION_REQUEST,
    LIST_SALES_BY_SKU_VISUALIZATION_SUCCESS,
    LIST_SALES_BY_SKU_VISUALIZATION_FAILURE,
  ],
  promise: (api: IApi) => api.ongoingReporting.listSalesBySkuVisualization(id, params),
  params,
}))

export const [
  EXPORT_SALES_BY_SKU_VISUALIZATION_REQUEST,
  EXPORT_SALES_BY_SKU_VISUALIZATION_SUCCESS,
  EXPORT_SALES_BY_SKU_VISUALIZATION_FAILURE,
] = createRequestTypes(prefix, 'EXPORT_SALES_BY_SKU_VISUALIZATION')

export const exportSalesBySkuVisualization = createAction((id: string, params: object) => ({
  type: [
    EXPORT_SALES_BY_SKU_VISUALIZATION_REQUEST,
    EXPORT_SALES_BY_SKU_VISUALIZATION_SUCCESS,
    EXPORT_SALES_BY_SKU_VISUALIZATION_FAILURE,
  ],
  promise: (api: IApi) =>
    api.ongoingReporting.listSalesBySkuVisualization(id, {
      ...params,
      isFile: true,
    }),
  params,
}))

export const [
  UPDATE_INVENTORY_MAPPING_REQUEST,
  UPDATE_INVENTORY_MAPPING_SUCCESS,
  UPDATE_INVENTORY_MAPPING_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_INVENTORY_MAPPING')

export const updateInventoryMapping = createAction((id: string, itemId: string, data: object) => ({
  type: [
    UPDATE_INVENTORY_MAPPING_REQUEST,
    UPDATE_INVENTORY_MAPPING_SUCCESS,
    UPDATE_INVENTORY_MAPPING_FAILURE,
  ],
  promise: (api: IApi) => api.ongoingReporting.updateInventoryMapping(id, itemId, data),
}))

const LIST_SALES_BY_SKU_FILTERS_TYPES = createRequestTypes(prefix, 'LIST_SALES_BY_SKU_FILTERS')
export const [
  LIST_SALES_BY_SKU_FILTERS_REQUEST,
  LIST_SALES_BY_SKU_FILTERS_SUCCESS,
  LIST_SALES_BY_SKU_FILTERS_FAILURE,
] = LIST_SALES_BY_SKU_FILTERS_TYPES

export const listSalesBySkuFilters = createAction((id: string, params: object) => ({
  type: LIST_SALES_BY_SKU_FILTERS_TYPES,
  promise: (api: IApi) => api.ongoingReporting.listSalesBySkuFilters(id, params),
  params,
}))

const LIST_SALES_BY_DISTRIBUTION_GRAPH_TYPES = createRequestTypes(
  prefix,
  'LIST_SALES_BY_DISTRIBUTION_GRAPH',
)
export const [
  LIST_SALES_BY_DISTRIBUTION_GRAPH_REQUEST,
  LIST_SALES_BY_DISTRIBUTION_GRAPH_SUCCESS,
  LIST_SALES_BY_DISTRIBUTION_GRAPH_FAILURE,
] = LIST_SALES_BY_DISTRIBUTION_GRAPH_TYPES

export const listSalesByDistributionGraph = createAction((id: string, params: object) => ({
  type: LIST_SALES_BY_DISTRIBUTION_GRAPH_TYPES,
  promise: (api: IApi) => api.ongoingReporting.listSalesByDistributionGraph(id, params),
  params,
}))

export const HIDE_SALES_DATA = `${prefix}/HIDE_SALES_DATA`

export const hideSalesData = () => ({
  type: HIDE_SALES_DATA,
})
